import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * /terms — Terms of Service (static page).
 * Matches the rest of the site's dark theme. Content is verbatim from the
 * authoritative TOS doc dated May 2026. If the underlying document
 * changes, update this file AND the "Last Updated" line below.
 */
const TermsPage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Terms of Service — AUDIVUE';
    return () => { document.title = prev; };
  }, []);

  return (
    <div
      className="min-h-screen text-zinc-200"
      style={{ background: '#050505' }}
      data-testid="terms-page"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-24">
        {/* Header */}
        <div className="flex flex-col items-center mb-10 text-center">
          <Link to="/" className="inline-block mb-6" data-testid="terms-home-link">
            <img
              src="/wordmark-tight.png"
              alt="AUDIVUE"
              className="h-6 md:h-7 w-auto object-contain"
            />
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-white">
            Terms of Service
          </h1>
          <p className="mt-3 text-xs uppercase tracking-[0.25em] text-zinc-500">
            Last Updated: May 2026
          </p>
        </div>

        {/* Body */}
        <article className="prose prose-invert prose-zinc max-w-none space-y-5 text-[15px] md:text-base leading-relaxed">
          <p className="text-zinc-300">
            Welcome to AUDIVUE. By accessing or using AUDIVUE ("Platform"),
            you agree to these Terms of Service ("Terms"). Please read them
            carefully. If you do not agree, do not use the Platform.
          </p>

          <Section title="1. Acceptance of Terms">
            By creating an account, uploading content, or using any feature
            of AUDIVUE, you confirm that you are at least 13 years old (or,
            if between 13 and 17, that you have obtained verifiable parental
            or guardian consent), that you have the legal authority to enter
            into these Terms, and that you will comply with all applicable
            laws and these Terms.
          </Section>

          <Section title="2. What AUDIVUE Is">
            AUDIVUE is a dedicated AI music video streaming and discovery
            platform. The Platform allows creators to upload AI-generated
            music videos and allows listeners to discover and stream content
            via algorithm-driven feeds. AUDIVUE is a streaming and discovery
            destination — not a social network, not a general video host,
            and not a music creation tool.
          </Section>

          <Section title="3. Account Types">
            <h3 className="text-base font-semibold text-white mt-4">3a. Listeners</h3>
            <p>
              Listeners may browse and stream content freely without creating
              an account. An account is required only to save content, follow
              creators, like videos, or comment. Listener accounts require a
              valid email address.
            </p>
            <h3 className="text-base font-semibold text-white mt-4">3b. Creators</h3>
            <p>Creators must complete the "Become a Creator" onboarding, which requires:</p>
            <ul className="list-disc pl-6 space-y-1 marker:text-zinc-500">
              <li>Verified email address</li>
              <li>Agreement to the Creator Content Policy below</li>
            </ul>
            <p>
              AUDIVUE reserves the right to suspend or terminate Creator
              accounts that violate these Terms.
            </p>
          </Section>

          <Section title="4. AI-Generated Content Policy">
            <p>
              AUDIVUE is an AI-content-only platform. All uploaded content
              must be AI-generated. By uploading, you represent and warrant
              that:
            </p>
            <ul className="list-disc pl-6 space-y-1 marker:text-zinc-500">
              <li>
                At least one primary element — the video, the audio, or both
                — must be AI-generated using tools such as Suno, Udio,
                Runway, Freebeat, Kaiber, or similar. Human-created elements
                are permitted alongside AI-generated ones.
              </li>
              <li>You hold all rights necessary to upload and distribute the content on AUDIVUE.</li>
              <li>
                The content does not reproduce, sample, or incorporate any
                copyrighted material without a valid license — including but
                not limited to existing songs, lyrics, melodies, or sound
                recordings.
              </li>
              <li>Style inspiration is permitted. Direct reproduction of protected expression is not.</li>
            </ul>
            <p>
              Uploads of non-AI-generated content, or AI content that
              incorporates unlicensed copyrighted material, will be removed
              and may result in account termination.
            </p>
          </Section>

          <Section title="5. Intellectual Property & Creator License">
            <p>You retain ownership of all original content you upload to AUDIVUE.</p>
            <p>
              By uploading content, you grant AUDIVUE a worldwide,
              non-exclusive, royalty-free license to host, store, display,
              stream, distribute, and promote your content on the Platform
              and in marketing materials. This license ends when you delete
              your content from the Platform, subject to cached or archived
              copies required for technical operations.
            </p>
            <p>You also grant other users the right to view and stream your content within the Platform.</p>
          </Section>

          <Section title="6. DMCA & Copyright Compliance">
            <p>
              AUDIVUE complies with the Digital Millennium Copyright Act
              (DMCA). If you believe content on the Platform infringes your
              copyright, please send a written notice to our designated DMCA
              agent including:
            </p>
            <ul className="list-disc pl-6 space-y-1 marker:text-zinc-500">
              <li>Your contact information</li>
              <li>Identification of the copyrighted work claimed to be infringed</li>
              <li>Identification of the allegedly infringing material and its location on the Platform</li>
              <li>A statement of good faith belief that the use is not authorized</li>
              <li>
                A statement, under penalty of perjury, that you are authorized
                to act on behalf of the copyright owner
              </li>
            </ul>
            <p>
              DMCA Agent: Courtney Visser<br />
              Email:{' '}
              <a
                href="mailto:support@audivue.io"
                className="text-orange-400 hover:text-orange-300 underline"
              >
                support@audivue.io
              </a>
            </p>
            <p>Repeat infringers will have their accounts terminated.</p>
          </Section>

          <Section title="7. Prohibited Conduct">
            <p>You may not use AUDIVUE to:</p>
            <ul className="list-disc pl-6 space-y-1 marker:text-zinc-500">
              <li>Upload content you do not have the right to distribute</li>
              <li>Upload non-AI-generated content</li>
              <li>Harass, threaten, or harm other users or creators</li>
              <li>Post spam, misleading metadata, or artificially inflate play counts</li>
              <li>Attempt to circumvent platform security, account verification, or content moderation</li>
              <li>Impersonate any person or entity</li>
              <li>Use the Platform in any manner that violates applicable local, state, national, or international law</li>
            </ul>
          </Section>

          <Section title="8. Monetization">
            AUDIVUE does not currently offer a monetization program. When a
            creator monetization program is introduced, Founding Creators
            (those who joined during the Platform's early access period)
            will be the first group engaged in discussions about program
            terms. No revenue share, payment, or compensation is guaranteed
            at this time.
          </Section>

          <Section title="9. Disclaimers">
            AUDIVUE is provided "as is" without warranty of any kind. We do
            not guarantee uninterrupted access, error-free operation, or
            that content on the Platform will meet your expectations. We
            are not responsible for user-generated content or any
            third-party tools used to create content uploaded to the
            Platform.
          </Section>

          <Section title="10. Limitation of Liability">
            To the maximum extent permitted by law, AUDIVUE and its
            founders, officers, employees, and agents shall not be liable
            for any indirect, incidental, special, consequential, or
            punitive damages arising out of your use or inability to use
            the Platform, even if we have been advised of the possibility
            of such damages. Our total liability for any claim arising from
            your use of the Platform shall not exceed the greater of the
            amount you paid AUDIVUE in the twelve (12) months preceding the
            claim, or fifty U.S. dollars ($50.00).
          </Section>

          <Section title="11. Indemnification">
            You agree to indemnify and hold harmless AUDIVUE and its
            affiliates from any claim, loss, damage, liability, or expense
            (including reasonable attorneys' fees) arising from your use of
            the Platform, your content, or your violation of these Terms.
          </Section>

          <Section title="12. Termination">
            AUDIVUE reserves the right to suspend or terminate your account
            at any time, with or without notice, for conduct that violates
            these Terms or is harmful to other users, AUDIVUE, or third
            parties. You may delete your account at any time.
          </Section>

          <Section title="13. Changes to These Terms">
            AUDIVUE reserves the right to update these Terms at any time.
            We will notify you of material changes by posting the updated
            Terms on the Platform and updating the "Last Updated" date.
            Continued use of the Platform after changes constitutes
            acceptance of the revised Terms.
          </Section>

          <Section title="14. Governing Law">
            These Terms are governed by the laws of the State of Texas,
            without regard to conflict of law principles. Any disputes
            arising from these Terms or your use of the Platform shall be
            resolved exclusively in the courts located in Houston, Texas.
          </Section>

          <Section title="15. Contact">
            <p>AUDIVUE</p>
            <p>Website: <a href="https://audivue.io" className="text-orange-400 hover:text-orange-300 underline">audivue.io</a></p>
            <p>Email: <a href="mailto:support@audivue.io" className="text-orange-400 hover:text-orange-300 underline">support@audivue.io</a></p>
          </Section>

          <p className="pt-4 text-zinc-400 italic">
            By using AUDIVUE, you acknowledge that you have read, understood,
            and agree to be bound by these Terms of Service.
          </p>
        </article>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-xl md:text-2xl font-semibold text-white mt-8">{title}</h2>
    {typeof children === 'string' ? <p>{children}</p> : children}
  </section>
);

export default TermsPage;
